import { useCallback, useEffect, useMemo, useState } from 'react';

import { ROLES } from '@/config';
import { useAuthContext } from '@/features/auth/AuthContext';
import type { AccountResponse } from '@/features/auth/api';
import { hasSuperadminRole } from '@/features/moderation/permissions';
import { useT } from '@/i18n/LocaleProvider';

import { type AccountListResponse, createAdminApiClient } from './api';

const PAGE_LIMIT = 25;

export function UserAdmin() {
  const t = useT();
  const auth = useAuthContext();
  const canAccess = hasSuperadminRole(auth.account);

  const apiClient = useMemo(() => createAdminApiClient(() => auth.token ?? null), [auth.token]);

  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [result, setResult] = useState<AccountListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingID, setUpdatingID] = useState<string | null>(null);

  const load = useCallback(
    async (nextPage: number, role: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await apiClient.listUsers(nextPage, PAGE_LIMIT, role || undefined);
        setResult(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('admin_users_error_load'));
      } finally {
        setIsLoading(false);
      }
    },
    [apiClient, t],
  );

  useEffect(() => {
    if (!canAccess) return;
    void load(page, roleFilter);
  }, [canAccess, page, roleFilter, load]);

  const handleFilterAll = useCallback(() => {
    setPage(1);
    setRoleFilter('');
  }, []);

  const handleFilterModerators = useCallback(() => {
    setPage(1);
    setRoleFilter(ROLES.moderator);
  }, []);

  const handleRoleToggle = useCallback(
    async (account: AccountResponse) => {
      const nextRole = account.role_id === ROLES.moderator ? ROLES.contributor : ROLES.moderator;

      setUpdatingID(account.id);

      try {
        await apiClient.setUserRole(account.id, nextRole);
        await load(page, roleFilter);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('admin_users_error_update'));
      } finally {
        setUpdatingID(null);
      }
    },
    [apiClient, load, page, roleFilter, t],
  );

  if (!canAccess) {
    return null;
  }

  const totalPages = result ? Math.ceil(result.pagination.total / PAGE_LIMIT) : 1;

  return (
    <div className="admin-users" data-testid="admin-users">
      <h1>{t('admin_users_title')}</h1>

      <div className="admin-users-filter" data-testid="admin-users-filter">
        <button aria-pressed={roleFilter === ''} type="button" onClick={handleFilterAll}>
          {t('admin_users_filter_all')}
        </button>
        <button
          aria-pressed={roleFilter === ROLES.moderator}
          type="button"
          onClick={handleFilterModerators}
        >
          {t('admin_users_filter_moderators')}
        </button>
      </div>

      {isLoading ? (
        <p data-testid="admin-users-loading">{t('admin_users_loading')}</p>
      ) : error ? (
        <p data-testid="admin-users-error">{error}</p>
      ) : result ? (
        <>
          <table data-testid="admin-users-table">
            <thead>
              <tr>
                <th>{t('admin_users_col_email')}</th>
                <th>{t('admin_users_col_name')}</th>
                <th>{t('admin_users_col_role')}</th>
                <th>{t('admin_users_col_created')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {result.accounts.map((account) => (
                <tr key={account.id} data-testid="admin-users-row">
                  <td>{account.email}</td>
                  <td>{account.email}</td>
                  <td>{account.role_id}</td>
                  <td>
                    {account.created_at ? new Date(account.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    {(account.role_id === ROLES.contributor ||
                      account.role_id === ROLES.moderator) && (
                      <button
                        data-testid="admin-users-role-toggle"
                        disabled={updatingID === account.id}
                        type="button"
                        onClick={() => void handleRoleToggle(account)}
                      >
                        {updatingID === account.id
                          ? t('admin_users_updating')
                          : account.role_id === ROLES.moderator
                            ? t('admin_users_demote')
                            : t('admin_users_promote')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="admin-users-pagination" data-testid="admin-users-pagination">
            <button disabled={page <= 1} type="button" onClick={() => setPage((p) => p - 1)}>
              {t('admin_users_prev')}
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              type="button"
              onClick={() => setPage((p) => p + 1)}
            >
              {t('admin_users_next')}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
